import { Phone, Mail, MapPin, Clock, Star, Shield, CheckCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/ui-custom/SEO';

const Contact = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFormLoading, setIsFormLoading] = useState(true);

  useEffect(() => {
    const existingLink = document.getElementById('jobber-css');
    if (!existingLink) {
      const link = document.createElement('link');
      link.id = 'jobber-css';
      link.rel = 'stylesheet';
      link.href = 'https://d3ey4dbjkt2f6s.cloudfront.net/assets/external/work_request_embed.css';
      link.media = 'screen';
      document.head.appendChild(link);
    }

    if (containerRef.current) {
      containerRef.current.innerHTML = '';
      
      const targetDiv = document.createElement('div');
      targetDiv.id = 'd4116b0c-e759-4dbf-aeb0-37290a3bcf6e-2201763';
      containerRef.current.appendChild(targetDiv);

      const script = document.createElement('script');
      script.src = 'https://d3ey4dbjkt2f6s.cloudfront.net/assets/static_link/work_request_embed_snippet.js';
      script.async = true;
      script.setAttribute('clienthub_id', 'd4116b0c-e759-4dbf-aeb0-37290a3bcf6e-2201763');
      script.setAttribute('form_url', 'https://clienthub.getjobber.com/client_hubs/d4116b0c-e759-4dbf-aeb0-37290a3bcf6e/public/work_request/embedded_work_request_form?form_id=2201763');
      script.onload = () => setIsFormLoading(false);

      containerRef.current.appendChild(script);
    }

    return () => {
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, []);

  const contactInfo = [
    {
      icon: Phone,
      title: 'Call Us',
      content: '(334) 692-5537',
      description: '8AM - 5PM, Monday-Friday',
      href: 'tel:3346925537',
    },
    {
      icon: Mail,
      title: 'Email Us',
      content: 'support@ardsdothan.com',
      description: 'We respond within 24 hours',
      href: 'mailto:support@ardsdothan.com',
    },
    {
      icon: MapPin,
      title: 'Service Area',
      content: 'Wiregrass Area, AL',
      description: 'Dothan, Enterprise, Ozark & more',
      href: '#service-areas',
    },
    {
      icon: Clock,
      title: 'Hours',
      content: 'Mon-Fri: 8AM-5PM',
      description: 'Emergency every day except Saturday',
      href: null,
    },
  ];

  const serviceAreas = [
    { name: 'Dothan', path: '/service-areas/dothan-al' },
    { name: 'Enterprise', path: '/service-areas/enterprise-al' },
    { name: 'Ozark', path: '/service-areas/ozark-al' },
    { name: 'Geneva', path: '/service-areas/geneva-al' },
    { name: 'Headland', path: '/service-areas/headland-al' },
    { name: 'Midland City', path: '/service-areas/midland-city-al' },
    { name: 'Daleville', path: '/service-areas/daleville-al' },
    { name: 'Newton', path: '/service-areas/newton-al' },
    { name: 'Rehobeth', path: '/service-areas/rehobeth-al' },
  ];

  return (
    <>
      <SEO
        title="Contact Ard's Plumbing & Electric | Dothan AL"
        description="Contact Ard's Plumbing & Electric serving Dothan, Enterprise, Ozark, and the Wiregrass, AL. Schedule service online or call (334) 692-5537. Mon-Fri 8AM-5PM."
        keywords="contact Ard's Plumbing, schedule plumber Dothan, book electrician, plumbing appointment, electrical service request"
        canonical="/contact-us"
        additionalSchema={{ '@context': 'https://schema.org', '@type': 'BreadcrumbList', 'itemListElement': [{ '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': 'https://ardsdothan.com/' }, { '@type': 'ListItem', 'position': 2, 'name': 'Contact', 'item': 'https://ardsdothan.com/contact-us' }] }}
      />

      {/* Hero Section */}
      <section className="bg-brand-blue pt-32 pb-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 text-white text-sm px-4 py-2 rounded-full mb-6">
            <Clock className="w-4 h-4" />
            Mon-Fri 8AM-5PM
          </div>
          <h1 className="font-heading font-black text-4xl lg:text-6xl text-white leading-tight mb-6">
            SCHEDULE<br />
            <span className="text-brand-yellow">SERVICE TODAY</span>
          </h1>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Book your plumbing or electrical service online, or give us a call. 
            We're here to help with all your home and business needs.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 px-4 bg-brand-navy">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((item, index) => (
              <div key={index} className="bg-white/10 rounded-2xl p-6 text-center">
                <div className="w-12 h-12 bg-brand-yellow/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-6 h-6 text-brand-yellow" />
                </div>
                <p className="text-white/70 text-sm mb-1">{item.title}</p>
                {item.href ? (
                  <a href={item.href} className="font-bold text-white text-lg hover:text-brand-yellow transition-colors">
                    {item.content}
                  </a>
                ) : (
                  <p className="font-bold text-white text-lg">{item.content}</p>
                )}
                <p className="text-white/60 text-sm mt-1">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Jobber Form Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading font-black text-3xl lg:text-5xl text-brand-black mb-4">
              BOOK ONLINE
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Fill out the form below and we'll get back to you as soon as possible 
              to confirm your appointment.
            </p>
          </div>

          {/* Jobber Embed Container */}
          <div className="bg-gray-50 rounded-3xl p-8 shadow-lg min-h-[400px] relative">
            {isFormLoading && (
              <div className="absolute inset-0 flex items-center justify-center rounded-3xl bg-gray-50">
                <div className="flex flex-col items-center gap-4 text-gray-500">
                  <div className="w-10 h-10 border-4 border-brand-blue border-t-transparent rounded-full animate-spin" />
                  <p className="text-sm font-medium">Loading booking form...</p>
                </div>
              </div>
            )}
            <div ref={containerRef}></div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading font-black text-3xl lg:text-5xl text-brand-black mb-6">
                WHY CHOOSE<br />
                <span className="text-brand-coral">ARD'S?</span>
              </h2>
              <p className="text-lg text-gray-700 mb-8">
                When you schedule service with Ard's Plumbing & Electric, 
                you're choosing a team that values your time and trust.
              </p>
              <ul className="space-y-4">
                {[
                  'Mon-Fri 8AM-5PM',
                  'Licensed and insured technicians',
                  'Upfront, honest pricing',
                  '30-day workmanship guarantee',
                  '65+ years combined experience',
                  'Family-owned local business',
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-gray-700">
                    <CheckCircle className="w-5 h-5 text-brand-blue flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl p-6 text-center shadow-sm">
                <Shield className="w-10 h-10 text-brand-blue mx-auto mb-3" />
                <p className="font-bold text-brand-black text-lg">Licensed</p>
                <p className="text-gray-500 text-sm">& Insured</p>
              </div>
              <div className="bg-white rounded-2xl p-6 text-center shadow-sm">
                <Clock className="w-10 h-10 text-brand-blue mx-auto mb-3" />
                <p className="font-bold text-brand-black text-lg">Mon-Fri</p>
                <p className="text-gray-500 text-sm">8AM-5PM</p>
              </div>
              <div className="bg-white rounded-2xl p-6 text-center shadow-sm">
                <Star className="w-10 h-10 text-brand-blue mx-auto mb-3" />
                <p className="font-bold text-brand-black text-lg">4+ Star</p>
                <p className="text-gray-500 text-sm">Rated</p>
              </div>
              <div className="bg-white rounded-2xl p-6 text-center shadow-sm">
                <MapPin className="w-10 h-10 text-brand-blue mx-auto mb-3" />
                <p className="font-bold text-brand-black text-lg">Local</p>
                <p className="text-gray-500 text-sm">Wiregrass</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section id="service-areas" className="py-20 px-4 bg-brand-yellow">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading font-black text-3xl lg:text-5xl text-brand-black mb-4">
              SERVICE AREAS
            </h2>
            <p className="text-lg text-brand-black/70 max-w-2xl mx-auto">
              Proudly serving communities throughout the Wiregrass area.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            {serviceAreas.map((city) => (
              <Link
                key={city.path}
                to={city.path}
                className="flex items-center gap-2 bg-white text-brand-black px-6 py-3 rounded-full font-medium hover:bg-brand-blue hover:text-white transition-colors"
              >
                <MapPin className="w-4 h-4" />
                {city.name}, AL
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Emergency CTA */}
      <section className="py-20 px-4 bg-brand-coral">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading font-black text-3xl lg:text-5xl text-white mb-6">
            HAVE AN EMERGENCY?
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            For urgent plumbing or electrical issues, call us directly. 
            Emergency services available every day except Saturday.
          </p>
          <a
            href="tel:3346925537"
            className="inline-flex items-center justify-center gap-3 px-10 py-5 rounded-full bg-white text-brand-coral font-bold text-xl hover:bg-gray-100 transition-colors"
          >
            <Phone className="w-6 h-6" />
            Call (334) 692-5537
          </a>
        </div>
      </section>
    </>
  );
};

export default Contact;